// Synchronised multi-shot session.
//
// The host is the time master. For every shot it picks a fire time on its own
// clock far enough in the future (countdown + network margin) and sends it to
// the guest. The guest converts it to its local clock with the NTP-style offset
// (net/clock.ts) — so both phones count down together and fire within a few
// ms of each other, independent of network latency at that moment.
//
// Each phone captures a *full-resolution* cut-out of itself (not the low-res
// live stream) and uploads it to the storage server, where HD / Ultra photos
// are rendered on demand. A small preview cut-out goes peer-to-peer to the
// whole room so everyone sees every shot instantly (booth/gallery.ts).

import { fireAt } from '../net/clock';
import { api, putFile, toRef } from '../api/client';
import type { BoothConfig, BurstPick, PreviewMeta, SessionInfo, ShotAck, ShotCmd } from '../net/protocol';
import { chooseOwn, choosePair, type BurstCand } from './burstpair';
import { decodePose } from './pose';
import type { Decision, LayoutState } from './solver';
import { registry } from '../media/registry';
import type { BoothRoom } from '../net/room';
import type { CameraPipeline, Cutout } from '../media/pipeline';
import type { RoomGallery } from './gallery';
import { sceneSlotOf, type PoseAdjust } from './layout';

export interface ShootUi {
  countdown(localTarget: number, index: number, total: number): void;
  flash(): void;
  /** Pause non-essential rendering until `untilMs` so the shutter timer is not delayed. */
  quiet(untilMs: number): void;
  shotDone(index: number, syncErrMs: number | null): void;
  status(msg: string): void;
}

/** Network margin added before the countdown so the command surely arrives first. */
export function scheduleMargin(rttMs: number) {
  return Math.min(1500, Math.max(400, rttMs * 3 + 250));
}

/** Rejection of the shots that were still pending when the shoot was stopped. */
export class ShootCancelled extends Error {
  constructor() { super('Đã dừng chụp'); this.name = 'ShootCancelled'; }
}

export class ShootController {
  private cancelFns: (() => void)[] = [];
  private acks = new Map<number, ShotAck>();
  private ackWaiters = new Map<number, (c: BurstCand[] | null) => void>();
  private pickWaiters = new Map<string, (p: BurstPick | null) => void>();

  /** Host: the guest's candidates for a shot (already there, or within `ms`). */
  private waitAckCands(index: number, ms: number): Promise<BurstCand[] | null> {
    const have = this.acks.get(index);
    if (have) return Promise.resolve(have.cands ?? null);
    return new Promise((resolve) => {
      const t = setTimeout(() => { this.ackWaiters.delete(index); resolve(null); }, ms);
      this.ackWaiters.set(index, (c) => { clearTimeout(t); this.ackWaiters.delete(index); resolve(c); });
    });
  }

  /** Guest: the host's choice for a shot (or null after `ms`: keep the own choice). */
  private waitPick(session: string, index: number, ms: number): Promise<BurstPick | null> {
    const key = `${session}:${index}`;
    return new Promise((resolve) => {
      const t = setTimeout(() => { this.pickWaiters.delete(key); resolve(null); }, ms);
      this.pickWaiters.set(key, (p) => { clearTimeout(t); this.pickWaiters.delete(key); resolve(p); });
    });
  }
  private localFired = new Map<number, number>();
  private uploads: Promise<unknown> = Promise.resolve();
  info: SessionInfo | null = null;
  running = false;
  /** saved room on the server (null = quick mode without account: previews only) */
  serverRoomId: string | null = null;
  /** called on the host once the session is stored on the server */
  onStored: ((sessionId: string) => void) | null = null;
  /** guest: the host stopped the shoot */
  onStopped: (() => void) | null = null;
  /**
   * v0.5 (US7): where the layer decision is frozen at the shutter. Defaults to the
   * live stage registered in media/registry.ts (Stage.snapshot()).
   */
  decisionSource: (() => { decision: Decision; layout: LayoutState } | null) | null = null;
  private layout: LayoutState | null = null;

  constructor(
    private room: BoothRoom,
    private pipe: CameraPipeline,
    private gallery: RoomGallery,
    private ui: ShootUi,
    private getAdjust: () => PoseAdjust,
    private mySlot: () => number,
    /** custom background to store with the session (host) */
    private customBgBlob: () => Blob | null,
  ) {
    room.on('shot', (cmd) => void this.onShotCmd(cmd));
    room.on('stopShoot', ({ session }) => {
      if (this.room.role !== 'guest' || session !== this.info?.session) return;
      this.cancel();
      this.onStopped?.();
    });
    room.on('session', (info) => void this.onSession(info));
    room.on('preview', ({ blob, meta }) => void this.onPreview(blob, meta));
    room.on('shotAck', (a) => {
      if (a.session !== this.info?.session) return;
      this.acks.set(a.index, a);
      this.reportSync(a.index);
      this.ackWaiters.get(a.index)?.(a.cands ?? null);
    });
    room.on('burstPick', (p) => this.pickWaiters.get(`${p.session}:${p.index}`)?.(p));
  }

  /**
   * Host: create the session (on the server when we are in a saved room), run all
   * shots, store the result.
   */
  async runHost(total: number, countdownSec: number, config: BoothConfig): Promise<SessionInfo> {
    this.running = true;
    try {
      let session = Math.random().toString(36).slice(2, 10);
      let key: string | null = null;
      if (this.serverRoomId) {
        try {
          const s = await api.createSession(this.serverRoomId, config, total);
          session = s.id;
          key = 'server';
          const bg = config.background === 'custom' ? this.customBgBlob() : null;
          if (bg) this.queueUpload(async () => {
            const t = await api.backgroundTicket(session);
            await putFile(t.uploadUrl, bg);
          });
        } catch (e) {
          console.warn('storage server unavailable', e);
          this.ui.status('Không lưu được lên máy chủ – chỉ có bản xem trước');
        }
      }
      const slots = this.room.partnerId ? [0, 1] : [0];
      const info: SessionInfo = { session, key, total, config, slots, startedAt: Date.now() };
      this.info = info;
      this.acks.clear();
      this.localFired.clear();
      this.gallery.startSession(info);
      this.room.sendSession(info);

      for (let i = 0; i < total && this.running; i++) {
        const rtt = this.room.lastStats?.rttMs ?? 150;
        const lead = countdownSec * 1000 + (i === 0 ? scheduleMargin(rtt) : 300);
        const cmd: ShotCmd = { session, index: i, total, atHost: this.room.hostNow() + lead, countdownMs: countdownSec * 1000 };
        // host pauses ML too while the guest runs its clock-sync burst
        this.pipe.holdMl(performance.now() + SYNC_BURST * SYNC_GAP + 400);
        this.room.sendShot(cmd);
        await this.fire(cmd);
        await sleep(500);
      }

      if (key) {
        await this.uploads.catch(() => {});
        try {
          const photos = (await api.finalize(session, config)).photos.map(toRef);
          this.gallery.setPhotos(photos);
          this.room.sendDone({ session, photos });
          this.onStored?.(session);
        } catch (e) {
          console.warn('finalize failed', e);
          this.ui.status('Lưu bộ ảnh lên máy chủ thất bại: ' + (e as Error).message);
        }
      }
      return info;
    } finally {
      this.running = false;
    }
  }

  /** v0.5: latest host layout (mirrorPhoto etc. are frozen with the decision at the shutter). */
  setLayout(s: LayoutState) {
    this.layout = s;
  }

  private snapshot(): Decision | null {
    const snap = (this.decisionSource ?? (() => registry.stage?.snapshot() ?? null))();
    if (!snap) return null;
    const mirrorPhoto = this.layout?.mirrorPhoto ?? snap.decision.mirrorPhoto;
    return { ...snap.decision, mirrorPhoto };
  }

  /** Stops the countdown / remaining shots; the host also stops the guest's phone. */
  cancel() {
    if (this.room.role === 'host' && this.running && this.info) this.room.sendStop(this.info.session);
    this.running = false;
    this.cancelFns.forEach((f) => f());
    this.cancelFns = [];
  }

  /** Both: countdown + capture at the given host time, then share/upload. */
  private fire(cmd: ShotCmd): Promise<Cutout> {
    return new Promise((resolve, reject) => {
      let timers: (() => void)[] = [];
      const arm = () => {
        timers.forEach((f) => f());
        const localTarget = this.room.hostToLocal(cmd.atHost);
        this.ui.countdown(localTarget, cmd.index, cmd.total);
        // keep the main thread idle around the shutter instant so the timer is precise
        const hold = setTimeout(() => this.pipe.holdMl(localTarget + 30), Math.max(0, localTarget - 400 - performance.now()));
        const quiet = setTimeout(() => this.ui.quiet(localTarget + 20), Math.max(0, localTarget - 150 - performance.now()));
        const cancel = fireAt(localTarget, (lateMs) => void shutter(lateMs));
        timers = [cancel, () => clearTimeout(hold), () => clearTimeout(quiet)];
        return localTarget;
      };
      const shutter = async (lateMs: number) => {
        const frame = this.pipe.grabFrame(); // the synchronised instant
        const decision = this.snapshot(); // same instant: layer order / contacts as seen live
        console.debug(`[shot ${cmd.index}] ${this.room.role} timer late by ${lateMs.toFixed(1)} ms`);
        this.ui.flash();
        const firedHost = this.room.role === 'host' ? frame.at : this.room.clock.localToHost(frame.at);
        this.localFired.set(cmd.index, firedHost);
        // shutter candidates on the host clock (metadata only)
        const local = await (frame.candidates ?? Promise.resolve([]));
        const cands: BurstCand[] = local.map((c) => ({ k: c.k, motion: c.motion, tHost: this.room.role === 'host' ? c.t : this.room.clock.localToHost(c.t) }));
        let pick: number | undefined;
        if (this.room.role === 'host') {
          this.ui.shotDone(cmd.index, null);
          this.reportSync(cmd.index);
          if (this.room.partnerId && cands.length) {
            // wait (briefly) for the guest's candidates, agree on one pair, tell the guest
            const theirs = await this.waitAckCands(cmd.index, 1200);
            const pair = theirs ? choosePair(cands, theirs) : null;
            if (pair) {
              pick = pair.a;
              this.room.sendPick({ session: cmd.session, index: cmd.index, guest: pair.b });
              console.debug(`[shot ${cmd.index}] burst pair skew ${pair.skew.toFixed(0)} ms${pair.withinSkew ? '' : ' (closest available)'}`);
            } else this.room.sendPick({ session: cmd.session, index: cmd.index, guest: null });
          }
        } else {
          this.room.sendShotAck({ session: cmd.session, index: cmd.index, firedHost, lateMs, cands });
          this.ui.shotDone(cmd.index, null);
          const p = await this.waitPick(cmd.session, cmd.index, 2000);
          if (p && p.guest !== null) pick = p.guest;
        }
        // solo / no agreement: the steadiest own frame near the instant
        if (pick === undefined && cands.length) pick = chooseOwn(cands, cmd.atHost) ?? undefined;
        try {
          // the member whose surroundings are the backdrop is not cut out
          const masked = sceneSlotOf(this.info?.config ?? { background: '' }) !== this.mySlot();
          const cut = await this.pipe.finishCutout(frame, masked, pick);
          this.share(cmd, cut, decision);
          resolve(cut);
        } catch (e) {
          reject(e);
        }
      };
      const first = arm();
      this.cancelFns.push(() => { timers.forEach((f) => f()); reject(new ShootCancelled()); });

      if (this.room.role === 'guest') {
        // Fresh clock samples taken while both phones pause ML (clean, low-jitter
        // round trips), then re-arm the shutter with the refined offset.
        const burstMs = SYNC_BURST * SYNC_GAP + 150;
        if (first - performance.now() > burstMs + 300) {
          this.pipe.holdMl(performance.now() + burstMs);
          void this.room.burstSync(SYNC_BURST, SYNC_GAP).then(() => {
            if (Math.abs(this.room.hostToLocal(cmd.atHost) - first) > 1) arm();
          });
        }
      }
    });
  }

  /** Own shot → local gallery, peers (preview) and storage server (full resolution). */
  private share(cmd: ShotCmd, cut: Cutout, decision: Decision | null = null) {
    const slot = this.mySlot();
    const adjust = { ...this.getAdjust() };
    const extra = { mirrored: cut.mirrored, noFace: cut.noFace };
    // v0.5: pose of the captured frame + the decision frozen at the shutter travel with the cut-out
    const v5 = {
      peerId: this.room.selfId,
      ...(cut.pose ? { pose: cut.pose } : {}),
      ...(decision ? { decision, mirrorPhoto: decision.mirrorPhoto !== false } : {}),
    };
    this.gallery.addPreview(cmd.session, cmd.index, slot, cut.previewBitmap, cut.face, adjust,
      { ...extra, id: v5.peerId, pose: cut.pose ? decodePose(cut.pose) : null, decision });
    const meta: PreviewMeta = { session: cmd.session, index: cmd.index, slot, face: cut.face, adjust, masked: cut.masked, ...extra, ...v5 };
    void this.room.sendPreview(cut.preview, meta);
    const key = this.info?.session === cmd.session ? this.info.key : null;
    if (key) {
      this.queueUpload(async () => {
        // the API keeps a bounded copy of this meta (backend SessionService.sanitizeMeta)
        const t = await api.cutoutTicket(cmd.session, cmd.index, {
          face: cut.face, adjust, masked: cut.masked, ...extra, ...v5, ...(cut.packed ? { packed: true } : {}),
        });
        await putFile(t.uploadUrl, cut.full);
        await api.cutoutDone(cmd.session, cmd.index);
        void this.pollShotFinal(cmd.session, cmd.index);
      });
    }
  }

  /**
   * v0.9.7: the server cuts out and composes each shot while the next one is being taken; as soon
   * as it is done, the tray and the result screen show it instead of the phone's quick preview.
   */
  private async pollShotFinal(session: string, index: number) {
    const t0 = Date.now();
    while (Date.now() - t0 < 60_000) {
      await sleep(1500);
      try {
        const r = await api.shotFinal(session, index);
        if (r.ready && r.url) { this.gallery.useServerImage(session, 'shot', index, r.url); return; }
      } catch { /* not there yet / offline: try again */ }
    }
  }

  /** Uploads run one after another, retried, without blocking the next shot. */
  private queueUpload(fn: () => Promise<unknown>) {
    this.uploads = this.uploads.then(async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          return await fn();
        } catch (e) {
          if (attempt === 2) this.ui.status('Tải ảnh lên máy chủ thất bại');
          await sleep(800 * (attempt + 1));
        }
      }
    });
  }

  /** Guest: host started a session. */
  private async onSession(info: SessionInfo) {
    if (this.room.role !== 'guest') return;
    this.info = info;
    this.acks.clear();
    this.localFired.clear();
    this.gallery.startSession(info);
    if (info.key && this.serverRoomId) {
      this.queueUpload(() => api.joinSession(info.session, this.mySlot()));
    }
  }

  /** Guest side. */
  private async onShotCmd(cmd: ShotCmd) {
    if (this.room.role !== 'guest') return;
    try {
      await this.fire(cmd);
    } catch (e) {
      if (!(e instanceof ShootCancelled)) console.warn('[shoot] guest shot failed', e);
    }
  }

  private async onPreview(blob: Blob, meta: PreviewMeta) {
    if (!this.gallery.hasSession(meta.session)) return;
    const bmp = await createImageBitmap(blob);
    this.gallery.addPreview(meta.session, meta.index, meta.slot, bmp, meta.face, meta.adjust, {
      mirrored: meta.mirrored !== false, noFace: !!meta.noFace,
      id: meta.peerId, pose: meta.pose ? decodePose(meta.pose) : null, decision: meta.decision ?? null,
    });
  }

  private reportSync(index: number) {
    const a = this.acks.get(index);
    const mine = this.localFired.get(index);
    if (a == null || mine == null) return; // called again when the other half arrives
    this.ui.shotDone(index, a.firedHost - mine);
  }
}

const SYNC_BURST = 6;
const SYNC_GAP = 50;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
